// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useState } from "react";
import { GitFork } from "lucide-react";
import { defaultPreferences } from "../../../shared/contracts";
import type { Diagnostic, Dialect, ModelDocument, ModelType, Operation, Preferences } from "../../../shared/contracts";

import { documentOperations, transformModel, allowedDerivationTargets, orderedBusinessKeys, logicalPerspective } from "../../../core";

import { Button, Diagnostics, Field, LossSummary, Modal, Notice, dialectLabel, errorText, modelTypeLabel } from "../../components/Primitives";

import { dialects } from "../../foundation/modelOptions.js";
export function TransformDialog({
  document,
  sourceRepresentationId,
  preferences = defaultPreferences,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  sourceRepresentationId?: string;
  preferences?: Preferences;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
}) {
  const targets = allowedDerivationTargets(document, sourceRepresentationId);
  const [target, setTarget] = useState<ModelType>(targets[0] ?? "logical");
  const [logicalPolicy, setLogicalPolicy] = useState(preferences.logicalPrimaryKeyPolicy ?? "business-first");
  const [removeSurrogates, setRemoveSurrogates] = useState(!!preferences.removeSurrogatesOnDerive);
  const [primaryKeys, setPrimaryKeys] = useState<Record<string,string>>({});
  const [confirmedSurrogateIds, setConfirmedSurrogateIds] = useState<string[]>([]);
  const [dialect, setDialect] = useState<Dialect>(
    document.model.targetProfile || "postgresql",
  );
  const sourcePhysical = document.representations.find(r=>r.representationId === (sourceRepresentationId || document.model.activeRepresentation))?.kind === "physical";
  const physicalTarget = target === "physical" || (target === "logical-physical" && !sourcePhysical);
  const [strategy, setStrategy] = useState("business-primary");
  const [result, setResult] = useState<any>();
  const [error, setError] = useState("");
  const [fieldTypes, setFieldTypes] = useState<Record<string, any>>({});
  const fields = document.objects.filter(
    (o) =>
      ["attribute", "column"].includes(o.kind) &&
      o.representationId ===
        (sourceRepresentationId || document.model.activeRepresentation),
  );
  const preview = () => {
    setError("");
    try {
      setResult(
        transformModel(document, target, dialect, {
          ...(physicalTarget ? { ...(target === "physical" ? { keyStrategy: strategy } : {}), fieldTypes } : {}),
          sourceRepresentationId,
          logicalPrimaryKeyPolicy: logicalPolicy,
          removeSurrogates: target === "logical" && removeSurrogates,
          primaryKeys,
          confirmedSurrogateIds,
        } as any),
      );
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      title="Derive a representation"
      subtitle="Preserve business meaning while choosing a target design."
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {result ? (
            <Button
              variant="primary"
              disabled={result.diagnostics?.some(
                (d: Diagnostic) => d.severity === "error",
              )}
              onClick={() => {
                const accepted = onOperations(
                  documentOperations(document, result.document),
                  `Derive ${modelTypeLabel(target)} representation`,
                );
                if (accepted !== false) onClose();
              }}
            >
              Apply reviewed derivation
            </Button>
          ) : (
            <Button icon={GitFork} variant="primary" onClick={preview}>
              Preview derivation
            </Button>
          )}
        </>
      }
    >
      <Notice>
        The original conceptual baseline stays distinct from generated views.
        Unresolved key and type decisions must be reviewed.
      </Notice>
      <div className="form-row">
        <Field label="Target model type">
          <select
            value={target}
            onChange={(e) => {
              setTarget(e.target.value as ModelType);
              setResult(undefined);
            }}
          >
            {targets.map((t) => (
              <option key={t} value={t}>
                {modelTypeLabel(t)}
              </option>
            ))}
          </select>
        </Field>
        {physicalTarget && <Field label="Database target">
          <select
            value={dialect}
            onChange={(e) => {
              setDialect(e.target.value as Dialect);
              setResult(undefined);
            }}
          >
            {dialects.map((d) => (
              <option key={d} value={d}>
                {dialectLabel(d)}
              </option>
            ))}
          </select>
        </Field>}
      </div>
      {(target === "logical" || target === "conceptual") && <>
        {target === "logical" && <><Field label="Logical primary-key policy"><select value={logicalPolicy} onChange={e=>{setLogicalPolicy(e.target.value as typeof logicalPolicy);setPrimaryKeys({});setResult(undefined);}}><option value="business-first">Promote first ordered business key</option><option value="retain-source">Retain source primary key</option></select></Field>
        <label className="check-row"><input type="checkbox" checked={removeSurrogates} onChange={e=>{setRemoveSurrogates(e.target.checked);setResult(undefined);}}/>Remove surrogate keys from derived logical model</label><p className="subtle">Removal is independent of PK promotion. Explicit surrogate metadata or your confirmation is required. Dependent fields stay in the proposal until valid business mappings are supplied.</p>
        {removeSurrogates && <details><summary>Confirm additional technical surrogate attributes</summary>{fields.map(field=><label key={field.objectId} className="check-row"><input type="checkbox" checked={confirmedSurrogateIds.includes(field.objectId)} onChange={e=>{setConfirmedSurrogateIds(ids=>e.target.checked ? [...ids,field.objectId] : ids.filter(id=>id!==field.objectId));setResult(undefined);}}/>{document.objects.find(o=>o.objectId===field.ownerObjectId)?.name}.{field.name}</label>)}</details>}</>}
        {(target === "conceptual" || logicalPolicy === "business-first") && document.objects.filter(o=>!o.ownerObjectId && o.representationId === (target === "conceptual" && document.model.modelType === "logical-physical" ? logicalPerspective(document,sourceRepresentationId)?.representationId : sourceRepresentationId || document.model.activeRepresentation)).map(owner=>{const groups=orderedBusinessKeys(document.objects.filter(o=>o.ownerObjectId===owner.objectId));return groups.length > 0 ? <Field key={owner.objectId} label={`${owner.name} business identifier`}><select value={primaryKeys[owner.objectId] ?? ""} onChange={e=>{setPrimaryKeys({...primaryKeys,[owner.objectId]:e.target.value});setResult(undefined);}}><option value="">First ordered group: {groups[0]!.name}</option>{groups.map((key,i)=><option key={key.objectId} value={key.objectId}>{i+1}. {key.name}: {key.members?.map(id=>document.objects.find(o=>o.objectId===id)?.name).join(' + ')}</option>)}</select></Field> : null;})}
      </>}
      {target === "logical-physical" && <Notice>The combined model retains source primary identifiers and business keys. Conceptual derivation uses its logical perspective.</Notice>}
      {physicalTarget && <>{target === "physical" && <Field label="Physical primary-key policy">
        <select
          value={strategy}
          onChange={(e) => {
            setStrategy(e.target.value);
            setResult(undefined);
          }}
        >
          <option value="business-primary">
            Use the selected business key
          </option>
          <option value="surrogate-primary">
            Generate a surrogate; preserve business uniqueness
          </option>
        </select>
      </Field>}
      <details className="details-card">
        <summary>
          Review field-type decisions <span>{fields.length} fields</span>
        </summary>
        {fields.map((f) => (
          <Field
            key={f.objectId}
            label={`${document.objects.find((o) => o.objectId === f.ownerObjectId)?.name || ""}.${f.name}`}
          >
            <input
              placeholder={f.type?.name || "Choose a concrete type"}
              value={fieldTypes[f.objectId]?.name || ""}
              onChange={(e) => {
                setFieldTypes({
                  ...fieldTypes,
                  [f.objectId]: { name: e.target.value },
                });
                setResult(undefined);
              }}
            />
          </Field>
        ))}
      </details></>}
      {error && <Notice kind="error">{error}</Notice>}
      {result && (
        <>
          <Diagnostics items={result.diagnostics || []} />
          <LossSummary loss={result.lossReport} />
          <div className="preview-counts">
            <span>
              {result.document.representations.length} representations
            </span>
            <span>{result.document.mappings.length} traceable mappings</span>
            <span>
              {documentOperations(document, result.document).length} proposed
              operations
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}
